import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useDateStyles from '../styles/DateAdjusterStyles';
import { useSyllabus } from '../../../contexts/SyllabusContext';
import Icon from '../../icons/Icon';
import DateTextField from '../../common/DateTextField';
import TimeTextField from '../../common/TimeTextField';

const DateAdjusterContent = ({ textInfo, daysOfWeek }) => {
  const classes = useDateStyles();
  const { actions } = useSyllabus();
  const { bulkSplitInstruction, firstAssignmentDue, lastAssignmentDue, assignmentsDueDays, bulkEditDescription } = textInfo;
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [selectedDays, setSelectedDays] = useState([]);

  const toggleDay = (dayKey) => {
    setSelectedDays((prev) => (prev.includes(dayKey) ? prev.filter((d) => d !== dayKey) : [...prev, dayKey]));
  };

  // Update context whenever parameters change
  useEffect(() => {
    if (actions && typeof actions.setDateAdjustParams === 'function') {
      actions.setDateAdjustParams({ startDate, startTime, endDate, endTime, selectedDays });
    }
  }, [startDate, startTime, endDate, endTime, selectedDays, actions]);

  return (
    <div className={classes.container}>
      <div className={classes.titleWrapper}>
        {bulkEditDescription &&
        <p className={classes.bulkEditDescription}>
          {bulkEditDescription}
        </p>
      }
        {bulkSplitInstruction &&
        <p className={classes.descriptionText}>
          {bulkSplitInstruction}
        </p>
      }
      </div>
      <div className={classes.dateAndDaysSection}>
        <div className={classes.rowsWrapper}>
          <div className={classes.dateTimeRow}>
            <div className={classes.dateFieldWrapper}>
              <div className={classes.bulkEditDateTextField}>
                <DateTextField
                  label={firstAssignmentDue}
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  parentApp="aet"
                  icons={{ calendar: <Icon name="CalendarIcon" /> }}
                />
              </div>
            </div>
            <div className={classes.timeFieldWrapper}>
              <div className={classes.dateTimeInputRow}>
                <TimeTextField
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  parentApp="aet"
                  icons={{ clock: <Icon name="ClockIcon" /> }}
                />
              </div>
            </div>
          </div>

          <div className={classes.dateTimeRow}>
            <div className={classes.dateFieldWrapper}>
              <div className={classes.bulkEditDateTextField}>
                <DateTextField
                  label={lastAssignmentDue}
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  parentApp="aet"
                  icons={{ calendar: <Icon name="CalendarIcon" /> }}
                />
              </div>
            </div>
            <div className={classes.timeFieldWrapper}>
              <div className={classes.dateTimeInputRow}>
                <TimeTextField
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  parentApp="aet"
                  icons={{ clock: <Icon name="ClockIcon" /> }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className={classes.weeksWrapper}>
          {assignmentsDueDays &&
          <p className={classes.questionText}>
            {assignmentsDueDays}
          </p>
          }
          <div className={classes.daysContainer}>
            {daysOfWeek?.map((day) => (
              <div
                key={day.key}
                className={classes.dayCheckbox}
                role="checkbox"
                aria-checked={selectedDays.includes(day.key)}
                tabIndex={0}
                onClick={() => toggleDay(day.key)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleDay(day.key);
                  }
                }}
              >
                <div className={`${classes.checkbox} ${selectedDays.includes(day.key) ? classes.checkboxChecked : ''}`}>
                  {selectedDays.includes(day.key) ? (
                    <Icon name="CheckboxCheckedIcon" />
                  ) : (
                    <Icon name="CheckboxOutlineIcon" />
                  )}
                </div>
                <span className={classes.dayLabel}>{day.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* <p className={classes.footerText}>
        We use AI to create assignments. You can always add and edit assignments later.
      </p> */}
    </div>
  );
};

DateAdjusterContent.propTypes = {
  textInfo: PropTypes.object,
  bulkSplitInstruction: PropTypes.string,
  firstAssignmentDue: PropTypes.string,
  lastAssignmentDue: PropTypes.string,
  assignmentsDueDays: PropTypes.string,
  bulkEditDescription: PropTypes.string,
  daysOfWeek: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  )
};

DateAdjusterContent.defaultProps = {
  textInfo: {},
  bulkSplitInstruction: '',
  firstAssignmentDue: '',
  lastAssignmentDue: '',
  assignmentsDueDays: '',
  bulkEditDescription: '',
  daysOfWeek: [
    { key: 'M', label: '' },
    { key: 'T', label: '' },
    { key: 'W', label: '' },
    { key: 'Th', label: '' },
    { key: 'F', label: '' },
    { key: 'S', label: '' },
    { key: 'Su', label: '' }
  ]
};

export default DateAdjusterContent;
